import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Sparkle, X, Printer } from '@phosphor-icons/react';
import { printReportCard } from '../lib/printReport';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const EDIT_ROLES = ['global_admin', 'school_admin', 'principal', 'registrar'];

const AttendanceStats = () => {
  const { data: ov } = useQuery({ queryKey: ['my-overview', ''], queryFn: () => api.get('/my/overview').then((r) => r.data) });
  if (!ov) return null;
  const a = ov.attendance;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 stagger-in" data-testid="academics-attendance-stats">
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Attendance · {ov.student.name}</p>
      <div className="flex items-center gap-5 flex-wrap">
        <div>
          <span className="font-display text-3xl font-extrabold" data-testid="academics-attendance-rate">{a.rate_30d != null ? `${a.rate_30d}%` : '—'}</span>
          <span className="text-xs text-slate-400 ms-2">last 30 days · {a.present_30d}/{a.total_30d} days present</span>
        </div>
        <div className="flex items-end gap-1 h-12" title="Weekly attendance">
          {a.weekly.map((w) => (
            <div key={w.week} className="flex flex-col items-center gap-0.5">
              <div className="w-6 bg-slate-100 rounded-sm flex items-end" style={{ height: 40 }}>
                <div className={`w-full rounded-sm ${w.pct >= 75 ? 'bg-green-500/80' : 'bg-rose-400'}`} style={{ height: `${(w.pct / 100) * 40}px` }} />
              </div>
              <span className="text-[8px] text-slate-400 font-mono-label">{w.week}</span>
            </div>
          ))}
          {a.weekly.length === 0 && <span className="text-xs text-slate-400">No attendance captured yet.</span>}
        </div>
      </div>
    </div>
  );
};

export default function Academics() {
  const { user } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const canEditTT = EDIT_ROLES.includes(user?.role);
  const isStudentOrGuardian = ['student', 'guardian'].includes(user?.role);
  const { data: classes } = useQuery({ queryKey: ['classes'], queryFn: () => api.get('/classes').then((r) => r.data), enabled: !isStudentOrGuardian });
  const [classId, setClassId] = useState(null);
  const cid = classId || classes?.[0]?.id;
  const { data: timetable } = useQuery({ queryKey: ['tt', cid], queryFn: () => api.get('/timetable', { params: { class_id: cid } }).then((r) => r.data), enabled: !!cid });
  const { data: reports } = useQuery({ queryKey: ['reports', cid], queryFn: () => api.get('/report-cards', { params: isStudentOrGuardian ? {} : { class_id: cid } }).then((r) => r.data), enabled: isStudentOrGuardian || !!cid });
  const { data: drafts } = useQuery({ queryKey: ['drafts'], queryFn: () => api.get('/ai/drafts').then((r) => r.data), enabled: ['teacher', 'principal', 'school_admin'].includes(user?.role) });
  const [draftForm, setDraftForm] = useState({ kind: 'lesson_plan', topic: '', grade: '5', subject: 'Mathematics' });
  const { data: teachers } = useQuery({ queryKey: ['teachers'], queryFn: () => api.get('/users', { params: { role: 'teacher' } }).then((r) => r.data), enabled: canEditTT });
  const { data: brand } = useQuery({ queryKey: ['branding'], queryFn: () => api.get('/branding').then((r) => r.data) });
  const doPrint = (r) => printReportCard(r, brand?.school_name || undefined, brand?.has_logo ? `${API}/branding/file/logo` : null);
  const [slotDlg, setSlotDlg] = useState(null); // {day, period}
  const [slotForm, setSlotForm] = useState({ subject: '', teacher_id: '' });

  const addSlotMut = useMutation({
    mutationFn: () => api.post('/timetable', { class_id: cid, day: slotDlg.day, period: slotDlg.period, subject: slotForm.subject, teacher_id: slotForm.teacher_id || null }),
    onSuccess: () => { toast.success('Slot added - no clashes'); setSlotDlg(null); setSlotForm({ subject: '', teacher_id: '' }); qc.invalidateQueries({ queryKey: ['tt'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delSlotMut = useMutation({
    mutationFn: (id) => api.delete(`/timetable/${id}`),
    onSuccess: () => { toast.success('Slot removed'); qc.invalidateQueries({ queryKey: ['tt'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const genMut = useMutation({
    mutationFn: () => api.post(`/report-cards/generate?class_id=${cid}&term=Term 1`),
    onSuccess: (r) => { toast.success(`${r.data.generated} report cards generated (draft)`); qc.invalidateQueries({ queryKey: ['reports'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rcAction = useMutation({
    mutationFn: ({ id, action }) => api.post(`/report-cards/${id}/${action}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }), onError: (e) => toast.error(errMsg(e)),
  });
  const draftMut = useMutation({
    mutationFn: () => api.post('/ai/drafts', { ...draftForm, grade: (classes || []).find((c) => c.id === cid)?.grade || draftForm.grade }),
    onSuccess: () => { toast.success('AI draft created - pending human approval'); qc.invalidateQueries({ queryKey: ['drafts'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const decideMut = useMutation({
    mutationFn: ({ id, d }) => api.post(`/ai/drafts/${id}/${d}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }), onError: (e) => toast.error(errMsg(e)),
  });

  if (isStudentOrGuardian) {
    return (
      <div data-testid="academics-page">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">{t('Academics')}</h1>
        <AttendanceStats />
        <h2 className="font-display text-lg font-bold mb-3">{t('My Report Cards')}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {(reports || []).map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-5 stagger-in card-hover" data-testid={`report-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-display font-bold">{r.student_name} - {r.term}</p>
                <button data-testid={`print-report-${r.id}`} onClick={() => doPrint(r)}
                  className="flex items-center gap-1 text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900 transition-colors shrink-0">
                  <Printer size={13} /> {t('Print')}
                </button>
              </div>
              <p className="font-display text-3xl font-extrabold mt-1">{r.overall_pct}%</p>
              <table className="w-full text-sm mt-3">
                <tbody>{r.rows.map((row) => <tr key={row.subject} className="border-t border-slate-100"><td className="py-1.5">{row.subject}</td><td className="py-1.5">{row.avg_pct}%</td><td className="py-1.5 font-bold">{row.grade}</td></tr>)}</tbody>
              </table>
            </div>
          ))}
          {(reports || []).length === 0 && <p className="text-slate-400">{t('No released report cards yet.')}</p>}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="academics-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">{t('Academics')}</h1>
      <div className="flex gap-2 mb-5 flex-wrap">
        {(classes || []).map((c) => (
          <button key={c.id} data-testid={`class-tab-${c.grade}${c.section}`} onClick={() => setClassId(c.id)}
            className={`border rounded-md px-4 py-2 text-sm font-medium transition-colors ${cid === c.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:border-slate-900'}`}>
            Grade {c.grade}{c.section} <span className="font-mono-label text-[10px] opacity-60">· {c.student_count} students</span>
          </button>
        ))}
      </div>

      <Tabs defaultValue="timetable">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="timetable" data-testid="acad-tab-timetable" className="h-auto py-2">{t('Timetable')}</TabsTrigger>
          <TabsTrigger value="reports" data-testid="acad-tab-reports" className="h-auto py-2">{t('Report cards')}</TabsTrigger>
          {['teacher', 'principal', 'school_admin'].includes(user?.role) && <TabsTrigger value="drafts" data-testid="acad-tab-drafts" className="h-auto py-2">{t('AI drafts')}</TabsTrigger>}
        </TabsList>

        <TabsContent value="timetable" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="timetable-grid">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr><th className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400 text-left">{t('Period')}</th>{DAYS.map((d) => <th key={d} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400 text-left">{t(d)}</th>)}</tr></thead>
              <tbody>
                {[1, 2, 3, 4].map((p) => (
                  <tr key={p} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-mono-label text-xs">{p}</td>
                    {DAYS.map((d) => {
                      const slot = (timetable || []).find((s) => s.day === d && s.period === p);
                      return (
                        <td key={d} className="py-1.5 px-2">
                          {slot ? (
                            <span className="inline-flex items-center gap-1.5 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1">
                              {slot.subject}
                              {canEditTT && (
                                <button data-testid={`slot-remove-${slot.id}`} onClick={() => delSlotMut.mutate(slot.id)} className="text-slate-400 hover:text-rose-600 transition-colors" title={t('Remove')}>
                                  <X size={11} />
                                </button>
                              )}
                            </span>
                          ) : canEditTT ? (
                            <button data-testid={`slot-add-${d}-${p}`} onClick={() => setSlotDlg({ day: d, period: p })}
                              className="text-slate-300 hover:text-slate-900 border border-dashed border-slate-200 hover:border-slate-900 rounded px-2 py-1 text-xs transition-colors">+ {t('Add slot')}</button>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mt-2">Conflict detection active: double-booking a class or teacher is rejected</p>
          <Dialog open={!!slotDlg} onOpenChange={(o) => !o && setSlotDlg(null)}>
            <DialogContent data-testid="slot-dialog">
              <DialogHeader><DialogTitle className="font-display">{t('Add slot')} - {slotDlg && `${t(slotDlg.day)} · ${t('Period')} ${slotDlg.period}`}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="slot-subject-input" placeholder={t('Subject')} value={slotForm.subject} onChange={(e) => setSlotForm({ ...slotForm, subject: e.target.value })} />
                <select data-testid="slot-teacher-select" value={slotForm.teacher_id} onChange={(e) => setSlotForm({ ...slotForm, teacher_id: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="">No teacher assigned</option>
                  {(teachers || []).map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
                </select>
                <Button data-testid="slot-save-button" disabled={!slotForm.subject || addSlotMut.isPending} onClick={() => addSlotMut.mutate()} className="w-full bg-slate-900 hover:bg-slate-800">{t('Add slot')}</Button>
                <p className="text-xs text-slate-400">Clashes for this class or the chosen teacher are rejected instantly with the conflicting slot named.</p>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <Button data-testid="generate-reports-button" onClick={() => genMut.mutate()} className="bg-slate-900 hover:bg-slate-800 mb-4">{t('Generate Term 1 report cards')}</Button>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(reports || []).map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-4 stagger-in card-hover" data-testid={`report-card-${r.id}`}>
                <div className="flex justify-between items-start">
                  <p className="font-semibold text-sm">{r.student_name}</p>
                  <span className={`text-[10px] font-mono-label uppercase px-2 py-0.5 rounded ${r.status === 'released' ? 'bg-green-100 text-green-800' : r.status === 'approved' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span>
                </div>
                <p className="font-display text-2xl font-extrabold">{r.overall_pct}%</p>
                <p className="text-xs text-slate-500 mb-2">{r.rows.map((x) => `${x.subject}: ${x.grade}`).join(' · ')}</p>
                {['principal', 'school_admin', 'global_admin'].includes(user?.role) && (
                  <div className="flex gap-2">
                    {r.status === 'draft' && <button data-testid={`rc-approve-${r.id}`} onClick={() => rcAction.mutate({ id: r.id, action: 'approve' })} className="text-xs bg-slate-900 text-white rounded px-2 py-1 hover:bg-slate-800">{t('Approve')}</button>}
                    {r.status === 'approved' && <button data-testid={`rc-release-${r.id}`} onClick={() => rcAction.mutate({ id: r.id, action: 'release' })} className="text-xs bg-green-700 text-white rounded px-2 py-1 hover:bg-green-600">{t('Release')}</button>}
                    {r.status === 'released' && (
                      <button data-testid={`print-report-${r.id}`} onClick={() => doPrint(r)} className="flex items-center gap-1 text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900 transition-colors"><Printer size={12} /> {t('Print')}</button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {(reports || []).length === 0 && <p className="text-slate-400 text-sm">No report cards for this class yet - generate them from marks.</p>}
          </div>
        </TabsContent>

        <TabsContent value="drafts" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap gap-2 items-end">
            <span className="font-mono-label text-[10px] uppercase bg-slate-100 text-slate-600 rounded px-2 py-1.5" data-testid="draft-grade-badge">
              {(() => { const g = (classes || []).find((c) => c.id === cid)?.grade; return g ? (/^\d+$/.test(g) ? `For Grade ${g}` : `For ${g}`) : 'Pick a class above'; })()}
            </span>
            <div className="flex-1 min-w-[200px]"><Input data-testid="draft-topic-input" placeholder="Topic (e.g. Photosynthesis)" value={draftForm.topic} onChange={(e) => setDraftForm({ ...draftForm, topic: e.target.value })} /></div>
            <select data-testid="draft-kind-select" value={draftForm.kind} onChange={(e) => setDraftForm({ ...draftForm, kind: e.target.value })} className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
              <option value="lesson_plan">Lesson plan</option><option value="question_paper">Question paper</option>
            </select>
            <Button data-testid="draft-generate-button" disabled={draftMut.isPending || !draftForm.topic} onClick={() => draftMut.mutate()} className="bg-blue-600 hover:bg-blue-500">
              <Sparkle size={14} weight="fill" className="mr-1" /> {draftMut.isPending ? 'Generating...' : 'Generate draft'}
            </Button>
          </div>
          <div className="space-y-3">
            {(drafts || []).map((d) => (
              <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-4 stagger-in card-hover" data-testid={`draft-${d.id}`}>
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-sm capitalize">{d.kind.replace('_', ' ')} - {d.topic}</p>
                    <p className="font-mono-label text-[10px] uppercase text-slate-400">grade {d.grade} · {d.subject} · {d.model} · by {d.created_by}</p>
                  </div>
                  <span className={`text-[10px] font-mono-label uppercase px-2 py-0.5 rounded ${d.status === 'approved' ? 'bg-green-100 text-green-800' : d.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'}`}>{d.status.replace(/_/g, ' ')}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap mt-2 text-slate-600 max-h-40 overflow-auto">{d.content}</p>
                {d.status === 'pending_approval' && (
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`draft-approve-${d.id}`} onClick={() => decideMut.mutate({ id: d.id, d: 'approve' })} className="text-xs bg-green-700 text-white rounded px-3 py-1.5 hover:bg-green-600">Approve & commit</button>
                    <button data-testid={`draft-reject-${d.id}`} onClick={() => decideMut.mutate({ id: d.id, d: 'reject' })} className="text-xs border border-slate-200 rounded px-3 py-1.5 hover:border-rose-600 hover:text-rose-600">Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
